import { Dispatch, SetStateAction } from 'react';

export type StepProps<T extends StepContext> = {
	context: T;
	setContext: Dispatch<SetStateAction<T>>;
	nextStep: () => void;
	screenId: string;
	onSubmit?: ({
		context,
		setContext,
	}: {
		context: T;
		setContext: Dispatch<SetStateAction<T>>;
	}) => Promise<void>;
	onSkip?: ({
		context,
		setContext,
	}: {
		context: T;
		setContext: Dispatch<SetStateAction<T>>;
	}) => Promise<void>;
	onLoad?: ({
		context,
		setContext,
	}: {
		context: T;
		setContext: Dispatch<SetStateAction<T>>;
	}) => Promise<void>;
};

export type SetupProps<T extends StepContext> = {
	screens: StepScreen<T>[];
	initialContext?: T;
	initialScreenIndex?: number;
};

type PreviewCardRowContents = {
	dueDate?: Date;
	itemStatus?: 'TO DO' | 'IN PROGRESS' | 'COMPLETE';
	summary?: string;
	type?: 'epic' | 'task';
};

type EpicRow = Omit<PreviewCardRowContents, 'type'> & { type: 'epic' };

export type KanbanPreviewCardData = {
	data?: {
		cardContents?: [EpicRow, ...PreviewCardRowContents[]];
		firstRowIsEpic?: boolean;
		rowCount?: number;
	};
	previewType: typeof TemplateOptionKanban | undefined;
};

export type ScrumPreviewCardData = {
	data?: {
		sprintCardContents?: PreviewCardRowContents[];
		sprintCardRowCount?: number;
		backlogCardContents?: PreviewCardRowContents[];
		backlogCardRowCount?: number;
	};
	previewType: typeof TemplateOptionScrum | undefined;
};

export type PreviewCardData = KanbanPreviewCardData | ScrumPreviewCardData;

export type StepContext = {
	taskId?: string;
	projectId?: string;
	tenantId: string;
	spaceName?: string;
	template?: typeof TemplateOptionScrum | typeof TemplateOptionKanban;
	teamType?: string;
	dueDate?: number;
	emailList?: string[];
	promisesToResolve: (Promise<void> | (() => Promise<void>))[];
	previewCardData?: PreviewCardData;
};

export const TemplateOptionScrum = 'SCRUM';
export const TemplateOptionKanban = 'KANBAN';

type StepScreen<T extends StepContext> = SplitStepScreen<T> | FullStepScreen<T>;

type SplitStepScreen<T extends StepContext> = {
	type: 'split';
	screenId: string;
	header?: string;
	subheader?: string;
	LeftComponent: React.ComponentType<StepProps<T>>;
	RightComponent: React.ComponentType<StepProps<T>>;
	onSubmit?: ({
		context,
		setContext,
	}: {
		context: T;
		setContext: Dispatch<SetStateAction<T>>;
	}) => Promise<void>;
	onSkip?: ({
		context,
		setContext,
	}: {
		context: T;
		setContext: Dispatch<SetStateAction<T>>;
	}) => Promise<void>;
	onLoad?: ({
		context,
		setContext,
	}: {
		context: T;
		setContext: Dispatch<SetStateAction<T>>;
	}) => Promise<void>;
};

type FullStepScreen<T extends StepContext> = {
	type: 'full';
	screenId: string;
	header?: string;
	subheader?: string;
	ScreenComponent: React.ComponentType<StepProps<T>>;
	onSubmit?: ({
		context,
		setContext,
	}: {
		context: T;
		setContext: Dispatch<SetStateAction<T>>;
	}) => Promise<void>;
	onSkip?: ({
		context,
		setContext,
	}: {
		context: T;
		setContext: Dispatch<SetStateAction<T>>;
	}) => Promise<void>;
	onLoad?: ({
		context,
		setContext,
	}: {
		context: T;
		setContext: Dispatch<SetStateAction<T>>;
	}) => Promise<void>;
};
